import {LOG_LEVELS, type Logger} from 'src/logger';
import {log} from 'src/logger/console';
import {OBSERVABILITY_CONFIG} from 'src/metrics/sail_observability';
import {ObservabilityAnalytics} from 'src/utils/observability/analytics';

/**
 * CommsObservabilityLogger is an implementation of the Logger interface that sends logs via [AEL](https://go/ael).
 *
 * It requires adding https://r.stripe.com to the `connect-src` of your [service's CSP configuration](https://go/wsp/csp).
 *
 * Also if your service is not hosted on a subdomain of stripe.com, b.stripecdn.com or dev.stripe.me, you will need to add your domain to the [client_config.yaml](https://git.corp.stripe.com/stripe-internal/zoolander/blob/bc6d6807f0c4c0db192e6e088af6651849d56c95/src/resources/com/stripe/dscore/analyticseventlogger/server/rpcserver/client_config.yaml#L4159)
 */
export class CommsObservabilityLogger implements Logger {
  client: ObservabilityAnalytics;

  /**
   * @param debugMode enables debug logging when true. Otherwise debug logs are no-ops.
   */
  constructor(private debugMode = false) {
    this.client = new ObservabilityAnalytics(OBSERVABILITY_CONFIG);
  }

  log(msg: string, ...rest: unknown[]): void {
    this.client.action('comms.log', {
      log_level: 'info',
      log_message: CommsObservabilityLogger.formatMessage(msg, rest),
    });
  }

  debug(msg: string, ...rest: unknown[]): void {
    if (!this.debugMode) {
      return;
    }
    // debug messages can potentially contain sensitive data e.g. logging the MessageEvent, so we never send them to AEL
    log(
      'DEBUG',
      LOG_LEVELS.DEBUG,
      CommsObservabilityLogger.formatMessage(msg, rest),
    );
  }

  warn(msg: string, ...rest: unknown[]): void {
    this.client.action(`comms.log`, {
      log_level: 'warn',
      log_message: CommsObservabilityLogger.formatMessage(msg, rest),
    });
  }

  error(msg: string, ...rest: unknown[]): void {
    this.client.action(`comms.log`, {
      log_level: 'error',
      log_message: CommsObservabilityLogger.formatMessage(msg, rest),
    });
  }

  private static formatMessage(msg: string, ...rest: unknown[]): string {
    if (rest.length === 0) {
      return msg;
    }

    const args = rest.map((arg) =>
      typeof arg === 'object' && arg !== null
        ? JSON.stringify(arg)
        : String(arg),
    );

    return `${msg} ${args.join(' ')}`;
  }
}
